import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import type { CaseUpdate, CaseView, CustomerUpdate, ProblemListItem } from "shared";
import { api } from "../api";
import { useAuth } from "../auth";
import { caseKey, type Interaction } from "../caseApi";
import { STATES } from "../states";

const URGENCIES = ["low", "normal", "high", "emergency"] as const;

/** Customer, problem, urgency, status controls, reassign (admins) and the contact history. */
export function CustomerPanel({ caseView, interactions }: { caseView: CaseView; interactions: Interaction[] }) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const closed = caseView.status === "closed";
  const refresh = () => qc.invalidateQueries({ queryKey: caseKey(caseView.id) });
  const problems = useQuery({ queryKey: ["problems"], queryFn: () => api<{ problems: ProblemListItem[] }>("/api/kb/problems") });
  const patch = useMutation({ mutationFn: (body: CaseUpdate) => api(`/api/cases/${caseView.id}`, { method: "PATCH", body }), onSuccess: refresh });
  // Customer fields go in the request body only, never in the URL.
  const patchCustomer = useMutation({
    mutationFn: (body: CustomerUpdate) => api(`/api/cases/${caseView.id}/customer`, { method: "PATCH", body }), onSuccess: refresh,
  });
  const canReassign = me?.user.role === "admin" || me?.user.role === "supervisor";
  const users = useQuery({
    queryKey: ["assignable-users"], enabled: canReassign,
    queryFn: () => api<{ users: { id: string; name: string }[] }>("/api/users/assignable"),
  });
  const reassign = useMutation({ mutationFn: (userId: string) => api(`/api/cases/${caseView.id}/reassign`, { method: "POST", body: { userId } }), onSuccess: refresh });

  const [kind, setKind] = useState("call");
  const [direction, setDirection] = useState("in");
  const [summary, setSummary] = useState("");
  const log = useMutation({
    mutationFn: () => api(`/api/cases/${caseView.id}/interactions`, { method: "POST", body: { kind, direction, ...(summary.trim() ? { summary: summary.trim() } : {}) } }),
    onSuccess: () => { setSummary(""); refresh(); },
  });
  const submit = (e: FormEvent) => { e.preventDefault(); log.mutate(); };
  const c = caseView.customer;
  const problem = problems.data?.problems.find((p) => p.id === caseView.problemId);

  return (
    <section className="card space-y-4 text-sm" aria-label="Customer">
      <div>
        <h2 className="text-base font-semibold">{c.name}</h2>
        <dl className="mt-1 space-y-0.5 text-slate-600 dark:text-slate-400">
          {c.phone && <div>{c.phone}</div>}
          {c.email && <div>{c.email}</div>}
          <div>{[c.stateCode, c.zip].filter(Boolean).join(" ")}{c.preferredContact ? ` · prefers ${c.preferredContact}` : ""}</div>
        </dl>
      </div>
      <div>
        <label className="label" htmlFor="customer-state">State</label>
        <select id="customer-state" aria-label="Customer state" className="input" disabled={closed || patchCustomer.isPending}
          value={c.stateCode ?? ""} onChange={(e) => e.target.value && patchCustomer.mutate({ stateCode: e.target.value })}>
          {!c.stateCode && <option value="">Not set</option>}
          {STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </select>
        {patchCustomer.isError && <p role="alert" className="mt-1 text-red-600">Could not update the customer.</p>}
      </div>
      <div>
        <div className="label">Problem</div>
        <div>{problem?.title ?? (caseView.problemId ? "…" : "Not set")}</div>
        <select aria-label="Change problem" className="input mt-1" disabled={closed || patch.isPending} value={caseView.problemId ?? ""}
          onChange={(e) => patch.mutate({ problemId: e.target.value || null })}>
          <option value="">No problem chosen</option>
          {problems.data?.problems.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </div>
      <div>
        <label className="label" htmlFor="case-urgency">Urgency</label>
        <select id="case-urgency" className="input" disabled={closed || patch.isPending} value={caseView.urgency}
          onChange={(e) => patch.mutate({ urgency: e.target.value as CaseUpdate["urgency"] })}>
          {URGENCIES.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>
      <div>
        <div className="label">Status: {caseView.status}</div>
        {closed ? (
          <button type="button" className="btn-primary" disabled={patch.isPending} onClick={() => patch.mutate({ status: "open" })}>Reopen</button>
        ) : (
          <div className="flex gap-2">
            <button type="button" className="btn-ghost border border-slate-300" disabled={patch.isPending}
              onClick={() => patch.mutate({ status: caseView.status === "waiting" ? "open" : "waiting" })}>
              {caseView.status === "waiting" ? "Mark open" : "Mark waiting"}</button>
            <button type="button" className="btn-ghost border border-slate-300" disabled={patch.isPending}
              onClick={() => patch.mutate({ status: "closed" })}>Close case</button>
          </div>
        )}
        {patch.isError && <p role="alert" className="mt-1 text-red-600">Could not update the case.</p>}
      </div>
      <div>
        <div className="label">Owner</div>
        <div>{caseView.owner.name}</div>
        {canReassign && users.data && (
          <select aria-label="Reassign case" className="input mt-1" value={caseView.owner.id} disabled={reassign.isPending}
            onChange={(e) => reassign.mutate(e.target.value)}>
            {users.data.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
      </div>
      <div>
        <div className="label">History</div>
        <ul className="space-y-1">
          {interactions.map((i) => (
            <li key={i.id}><span className="text-xs text-slate-500">{new Date(i.createdAt).toLocaleString()} · {i.direction === "in" ? "incoming" : "outgoing"} {i.kind}</span>
              {i.summary && <div>{i.summary}</div>}</li>
          ))}
          {interactions.length === 0 && <li className="text-slate-500">No contact logged yet.</li>}
        </ul>
        {!closed && (
          <form onSubmit={submit} className="mt-2 space-y-1">
            <div className="flex gap-2">
              <select aria-label="Contact kind" className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="call">Call</option><option value="email">Email</option><option value="sms">Text</option><option value="note">Note</option></select>
              <select aria-label="Contact direction" className="input" value={direction} onChange={(e) => setDirection(e.target.value)}>
                <option value="in">Incoming</option><option value="out">Outgoing</option></select>
            </div>
            <input aria-label="Contact summary" className="input" maxLength={1000} placeholder="Short summary" value={summary} onChange={(e) => setSummary(e.target.value)} />
            <button type="submit" className="btn-ghost border border-slate-300" disabled={log.isPending}>Log contact</button>
          </form>
        )}
      </div>
    </section>
  );
}
